import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Mail, Save, Check, Loader2, ExternalLink, RefreshCw, X, Users } from "lucide-react";
import FeedbackButtons, { LEAD_REASONS } from "@/components/FeedbackButtons";
import { feedbackContextFor } from "./leadModel";
import { BuyingCommitteeModal } from "./BuyingCommitteeModal";

export const AccountThreads = ({ lead, index = 0, userId }) => {
  const [open, setOpen] = useState(false);
  const also = lead?.alsoAt || [];
  if (!lead?.multiThreadReason && !also.length) return null;
  const preview = also.slice(0, 2).map((a) => a.personTitle || a.personName).filter(Boolean).join(", ");
  return (
    <div className="mb-3 space-y-1" data-testid={`account-threads-${index}`}>
      {lead.multiThreadReason && (
        <p className="inline-flex items-center gap-1 text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-0.5" data-testid={`multi-thread-reason-${index}`}>
          <Users className="h-3 w-3" /> Second thread · {lead.multiThreadReason}
        </p>
      )}
      {also.length > 0 && (
        <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-ism-teal bg-white/[0.03] hover:bg-ism-teal/[0.08] border border-white/10 hover:border-ism-teal/30 rounded-md px-2 py-0.5 transition-colors"
          data-testid={`also-at-${index}`}>
          <Users className="h-3 w-3" /> +{also.length} more buyer{also.length > 1 ? "s" : ""} at {lead.companyName || "this firm"}{preview ? ` (${preview}${also.length > 2 ? ", …" : ""})` : ""} · view buying committee
        </button>
      )}
      {open && <BuyingCommitteeModal lead={lead} userId={userId} open={open} onClose={() => setOpen(false)} />}
    </div>
  );
};

export const CompanyFacts = ({ facts, registry, index = 0, compact = false }) => {
  if (!facts?.length && !registry) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "mb-2" : "mb-3"}`} data-testid={`company-facts-${index}`}>
      {facts.map((f, i) => (
        <span key={f} className="inline-flex items-center rounded-md bg-white/[0.04] border border-white/10 px-2 py-0.5 text-[11px] text-gray-300" data-testid={`company-fact-${index}-${i}`}>
          {f}
        </span>
      ))}
      {registry?.url && (
        <a href={registry.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-ism-teal hover:underline" data-testid={`registry-link-${index}`}>
          <ExternalLink className="h-3 w-3" />
          {registry.label} record
        </a>
      )}
    </div>
  );
};

export const InferredBuyer = ({ buyer, index = 0, compact = false }) => {
  if (!buyer) return null;
  return (
    <div className={`rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 ${compact ? "mb-2" : "mb-3"}`} data-testid={`inferred-buyer-${index}`}>
      <p className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-0.5">Likely buyer · AI-inferred, not verified</p>
      <p className="text-sm text-amber-100 font-medium" data-testid={`inferred-buyer-title-${index}`}>{buyer.title}</p>
      {buyer.rationale && <p className="text-xs text-gray-400 mt-0.5">{buyer.rationale}</p>}
    </div>
  );
};

export const IcpBadge = ({ score, color }) =>
  score ? <span className={`text-xs px-2 py-0.5 rounded-full border ${color}`}>{score}% ICP</span> : null;

export const EmailPill = ({ email, confidence, compact = false }) => {
  if (!email) return null;
  if (compact) {
    return (
      <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-ism-teal/10 border border-ism-teal/20">
        <Mail className="h-3 w-3 text-ism-teal" />
        <span className="text-xs text-ism-teal font-mono">{email}</span>
        {confidence && <span className="text-xs text-ism-teal/60">({confidence}%)</span>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-ism-teal/10 border border-ism-teal/20 w-fit">
      <Mail className="h-4 w-4 text-ism-teal" />
      <span className="text-sm text-ism-teal font-medium">{email}</span>
      {confidence && <span className="text-xs text-gray-400">({confidence}%)</span>}
    </div>
  );
};

export const SaveButton = ({ onClick, isSaving, isSaved, testId }) => (
  <Button onClick={onClick} disabled={isSaving || isSaved} size="sm" variant="ghost"
    className={`h-7 px-2 ${isSaved ? "text-ism-teal" : "text-gray-400 hover:text-white"}`} data-testid={testId}>
    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : isSaved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
  </Button>
);

export const DismissButton = ({ onClick, testId }) => (
  <Button onClick={onClick} size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-red-400" data-testid={testId} title="Dismiss">
    <X className="h-4 w-4" />
  </Button>
);

export const LeadFeedback = ({ lead, index, userId, agentType, actionId, name }) => (
  <FeedbackButtons userId={userId} agentType={agentType} actionId={actionId} outputSummary={name}
    context={feedbackContextFor(lead, index)} reasons={LEAD_REASONS} size="small" testIdPrefix={`lead-${index}`} />
);

export const RefreshIntelButton = ({ onClick, isRefreshing }) => (
  <Button onClick={(e) => { e.stopPropagation(); onClick(); }} disabled={isRefreshing} size="sm" variant="ghost"
    className="h-6 px-2 text-xs text-gray-400 hover:text-ism-teal">
    {isRefreshing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
    {isRefreshing ? "Refreshing..." : "Refresh Intel"}
  </Button>
);

export const TitleLine = ({ title, company, compact = false }) => {
  if (!title && !company) return null;
  return (
    <p className={`text-sm text-gray-400 ${compact ? "mb-2" : "mb-3"}`}>
      {title}
      {title && company && <span className={compact ? "" : "text-gray-500"}> at </span>}
      {company && <span className="text-gray-300">{company}</span>}
    </p>
  );
};
